import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Chip,
    Alert,
    Snackbar,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import userService from '../services/userService';
import { UserDTO, CreateUserRequest, UserRole } from '../types/user.types';

const UserSettingsPage: React.FC = () => {
    const [users, setUsers] = useState<UserDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalElements, setTotalElements] = useState(0);

    // Dialog states
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [selectedUser, setSelectedUser] = useState<UserDTO | null>(null);

    // Delete confirmation
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<UserDTO | null>(null);

    // Snackbar
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    // Form state
    const [formData, setFormData] = useState<CreateUserRequest>({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        role: UserRole.NURSE,
        profilePictureUrl: '',
        employeeId: '',
        licenseNumber: '',
        department: '',
        jobTitle: '',
        hireDate: '',
        notes: '',
    });

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        fetchUsers();
    }, [page, rowsPerPage]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await userService.getAllUsers(page, rowsPerPage);
            setUsers(response.content);
            setTotalElements(response.totalElements);
        } catch (error) {
            showSnackbar('Failed to fetch users', 'error');
        } finally {
            setLoading(false);
        }
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.username || formData.username.trim().length < 3) {
            errors.username = 'Username must be at least 3 characters';
        } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
            errors.username = 'Username must be alphanumeric with underscores only';
        }

        if (!formData.email) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Invalid email format';
        }

        if (!formData.firstName || formData.firstName.trim().length < 2) {
            errors.firstName = 'First name must be at least 2 characters';
        }

        if (!formData.lastName || formData.lastName.trim().length < 2) {
            errors.lastName = 'Last name must be at least 2 characters';
        }

        if (formData.phone && !/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
            errors.phone = 'Invalid phone format';
        }

        if (!formData.role) {
            errors.role = 'Role is required';
        }

        if (formData.employeeId && !/^[a-zA-Z0-9]+$/.test(formData.employeeId)) {
            errors.employeeId = 'Employee ID must be alphanumeric';
        }

        if (formData.department && formData.department.trim().length < 2) {
            errors.department = 'Department must be at least 2 characters';
        }

        if (formData.jobTitle && formData.jobTitle.trim().length < 2) {
            errors.jobTitle = 'Job title must be at least 2 characters';
        }

        if (formData.hireDate) {
            const hireDate = new Date(formData.hireDate);
            const today = new Date();
            if (hireDate > today) {
                errors.hireDate = 'Hire date cannot be in the future';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleOpenDialog = (mode: 'create' | 'edit', user?: UserDTO) => {
        setDialogMode(mode);
        if (mode === 'edit' && user) {
            setSelectedUser(user);
            setFormData({
                username: user.username,
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                phone: user.phone || '',
                role: user.role || UserRole.NURSE,
                profilePictureUrl: user.profilePictureUrl || '',
                employeeId: user.employeeId || '',
                licenseNumber: user.licenseNumber || '',
                department: user.department || '',
                jobTitle: user.jobTitle || '',
                hireDate: user.hireDate || '',
                notes: user.notes || '',
            });
        } else {
            setSelectedUser(null);
            setFormData({
                username: '',
                email: '',
                firstName: '',
                lastName: '',
                phone: '',
                role: UserRole.NURSE,
                profilePictureUrl: '',
                employeeId: '',
                licenseNumber: '',
                department: '',
                jobTitle: '',
                hireDate: '',
                notes: '',
            });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedUser(null);
        setFormErrors({});
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            if (dialogMode === 'create') {
                await userService.createUser(formData);
                showSnackbar('User created successfully', 'success');
            } else if (selectedUser) {
                await userService.updateUser(selectedUser.id!, formData);
                showSnackbar('User updated successfully', 'success');
            }
            handleCloseDialog();
            fetchUsers();
        } catch (error: any) {
            showSnackbar(error.response?.data?.message || 'Operation failed', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (user: UserDTO) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;

        try {
            setLoading(true);
            await userService.deleteUser(userToDelete.id!);
            showSnackbar('User deleted successfully', 'success');
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            fetchUsers();
        } catch (error: any) {
            showSnackbar(error.response?.data?.message || 'Failed to delete user', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getRoleColor = (role?: UserRole | string) => {
        const roleColors: Record<string, 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
            [UserRole.SUPER_ADMIN]: 'error',
            [UserRole.CLIENT_ADMIN]: 'warning',
            [UserRole.FACILITY_ADMIN]: 'info',
            'FACILITY_MANAGER': 'info',
            [UserRole.ADMIN]: 'primary',
            [UserRole.NURSE]: 'success',
            [UserRole.DOCTOR]: 'secondary',
            [UserRole.SOCIAL_WORKER]: 'info',
            [UserRole.PHARMACIST]: 'primary',
            [UserRole.BILLING_SPECIALIST]: 'warning',
        };
        return (role && roleColors[role]) || 'default';
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                    User Settings
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog('create')}
                    sx={{
                        bgcolor: "#48CAE4",
                        color: "#000000",
                        fontWeight: "normal",
                        textTransform: "none",
                        "&:hover": { bgcolor: "#3ab3cc" }
                    }}
                >
                    Add User
                </Button>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <TableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Department</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No users found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((user) => (
                                        <TableRow key={user.id} hover>
                                            <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                                            <TableCell>{user.username}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={(() => {
                                                        const r = (user.role || (user.roles && user.roles.length > 0 ? user.roles[0] : 'N/A')).toUpperCase();
                                                        if (r === 'NURSE' || r === 'CAREGIVER') return 'Caregiver';
                                                        if (r === 'LEAD_CAREGIVER') return 'Lead Caregiver';
                                                        if (r === 'FACILITY_ADMIN' || r === 'FACILITY_MANAGER') return 'Facility Manager';
                                                        return r.replace(/_/g, ' ');
                                                    })()}
                                                    size="small"
                                                    color={getRoleColor(user.role || (user.roles && user.roles.length > 0 ? user.roles[0] : undefined))}
                                                />
                                            </TableCell>
                                            <TableCell>{user.department || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.isActive ? 'Active' : 'Inactive'}
                                                    color={user.isActive ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleOpenDialog('edit', user)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(user)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalElements}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: '#48CAE4', color: '#000000', fontWeight: 'bold' }}>
                    {dialogMode === 'create' ? 'Create New User' : 'Edit User'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                label="First Name *"
                                value={formData.firstName}
                                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                error={!!formErrors.firstName}
                                helperText={formErrors.firstName}
                                fullWidth
                            />
                            <TextField
                                label="Last Name *"
                                value={formData.lastName}
                                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                error={!!formErrors.lastName}
                                helperText={formErrors.lastName}
                                fullWidth
                            />
                        </Box>
                        <TextField
                            label="Username *"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                            error={!!formErrors.username}
                            helperText={formErrors.username}
                            fullWidth
                        />
                        <TextField
                            label="Email *"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            error={!!formErrors.email}
                            helperText={formErrors.email}
                            fullWidth
                        />
                        <TextField
                            label="Phone"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            error={!!formErrors.phone}
                            helperText={formErrors.phone}
                            fullWidth
                        />
                        <FormControl fullWidth error={!!formErrors.role}>
                            <InputLabel>Role *</InputLabel>
                            <Select
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                                label="Role *"
                            >
                                <MenuItem value={UserRole.SUPER_ADMIN}>Super Admin</MenuItem>
                                <MenuItem value={UserRole.CLIENT_ADMIN}>Client Admin</MenuItem>
                                <MenuItem value={UserRole.FACILITY_ADMIN}>Facility Manager</MenuItem>
                                <MenuItem value={UserRole.ADMIN}>Admin</MenuItem>
                                <MenuItem value={UserRole.NURSE}>Nurse</MenuItem>
                                <MenuItem value={UserRole.DOCTOR}>Doctor</MenuItem>
                                <MenuItem value={UserRole.SOCIAL_WORKER}>Social Worker</MenuItem>
                                <MenuItem value={UserRole.PHARMACIST}>Pharmacist</MenuItem>
                                <MenuItem value={UserRole.BILLING_SPECIALIST}>Billing Specialist</MenuItem>
                            </Select>
                            {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
                        </FormControl>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                label="Employee ID"
                                value={formData.employeeId}
                                onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                                error={!!formErrors.employeeId}
                                helperText={formErrors.employeeId}
                                fullWidth
                            />
                            <TextField
                                label="License Number"
                                value={formData.licenseNumber}
                                onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                                fullWidth
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                label="Department"
                                value={formData.department}
                                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                error={!!formErrors.department}
                                helperText={formErrors.department}
                                fullWidth
                            />
                            <TextField
                                label="Job Title"
                                value={formData.jobTitle}
                                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                                error={!!formErrors.jobTitle}
                                helperText={formErrors.jobTitle}
                                fullWidth
                            />
                        </Box>
                        <TextField
                            label="Hire Date"
                            type="date"
                            value={formData.hireDate}
                            onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                            error={!!formErrors.hireDate}
                            helperText={formErrors.hireDate}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="Notes"
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : dialogMode === 'create' ? 'Create' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete user "{userToDelete?.firstName} {userToDelete?.lastName}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserSettingsPage;